import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../../mutation-types';
import KanbanListsAPI from '../../../api/kanbanLists';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getListsByBoard: _state => boardId =>
    _state.records
      .filter(list => list.kanban_board_id === Number(boardId))
      .sort((a, b) => a.position - b.position),
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }, kanbanBoardId) => {
    commit(types.SET_KANBAN_LISTS_UI_FLAG, { isFetching: true });
    try {
      const response = await KanbanListsAPI.get(kanbanBoardId);
      commit(types.SET_KANBAN_LISTS, response.data);
    } finally {
      commit(types.SET_KANBAN_LISTS_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit }, listObj) => {
    commit(types.SET_KANBAN_LISTS_UI_FLAG, { isCreating: true });
    try {
      const response = await KanbanListsAPI.create(listObj);
      commit(types.ADD_KANBAN_LIST, response.data);
      return response.data;
    } finally {
      commit(types.SET_KANBAN_LISTS_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_KANBAN_LISTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await KanbanListsAPI.update(id, updateObj);
      commit(types.EDIT_KANBAN_LIST, response.data);
      return response.data;
    } finally {
      commit(types.SET_KANBAN_LISTS_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_KANBAN_LISTS_UI_FLAG, { isDeleting: true });
    try {
      await KanbanListsAPI.delete(id);
      commit(types.DELETE_KANBAN_LIST, id);
    } finally {
      commit(types.SET_KANBAN_LISTS_UI_FLAG, { isDeleting: false });
    }
  },

  reorder: async (
    { commit, state: _state },
    { kanbanBoardId, positionsHash }
  ) => {
    // Keep the old positions so a failed request can roll the optimistic move back.
    const oldPositions = Object.keys(positionsHash).reduce((map, id) => {
      map[id] = _state.records.find(list => list.id === Number(id))?.position;
      return map;
    }, {});
    // Show the drop straight away; this position is only the client's midpoint guess.
    commit(types.SET_KANBAN_LIST_POSITIONS, positionsHash);
    try {
      const response = await KanbanListsAPI.reorder({
        kanbanBoardId,
        positionsHash,
      });
      // Adopt the backend's re-spaced positions (mirrors helpCenterArticles/reorder). The midpoint
      // sent above can tie an existing list, and the server's rebalance is what breaks the tie.
      if (response.data?.positions) {
        commit(types.SET_KANBAN_LIST_POSITIONS, response.data.positions);
      }
    } catch (error) {
      commit(types.SET_KANBAN_LIST_POSITIONS, oldPositions);
      throw error;
    }
  },

  archiveCards: async (_ctx, listId) => {
    await KanbanListsAPI.archiveCards(listId);
  },
};

export const mutations = {
  [types.SET_KANBAN_LISTS_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.SET_KANBAN_LISTS]: MutationHelpers.set,
  [types.ADD_KANBAN_LIST]: MutationHelpers.create,
  [types.EDIT_KANBAN_LIST]: MutationHelpers.update,
  [types.DELETE_KANBAN_LIST]: MutationHelpers.destroy,
  // Patches positions by id, leaving every other field alone (mirrors SET_ARTICLE_POSITIONS).
  [types.SET_KANBAN_LIST_POSITIONS](_state, positionsHash) {
    _state.records = _state.records.map(list => {
      const position = positionsHash[list.id];
      return position === undefined ? list : { ...list, position };
    });
  },
};

export default { namespaced: true, state, getters, actions, mutations };
